/**
 * Gmail API service.
 * Uses Google Workspace MCP Gateway for data fetching.
 * All helper functions (parsing, decoding) remain client-side.
 */
import { mcpCallOrThrow, isMcpConnected } from './googleMcp'
const GMAIL_API = 'https://gmail.googleapis.com/gmail/v1'
export interface GmailThread {
  id: string
  historyId: string
  snippet: string
  messages?: GmailMessage[]
}
export interface GmailMessage {
  id: string
  threadId: string
  labelIds: string[]
  snippet: string
  internalDate: string
  payload: {
    headers: Array<{
      name: string
      value: string
    }>
    mimeType: string
    body?: {
      size: number
      data?: string
    }
    parts?: Array<{
      mimeType: string
      filename?: string
      body?: {
        size: number
        data?: string
        attachmentId?: string
      }
      parts?: Array<{
        mimeType: string
        filename?: string
        body?: {
          size: number
          data?: string
          attachmentId?: string
        }
      }>
    }>
  }
  sizeEstimate: number
}
export interface GmailLabel {
  id: string
  name: string
  type: string
  messagesTotal: number
  messagesUnread: number
}
export interface GmailAttachment {
  attachmentId: string
  filename: string
  mimeType: string
  size: number
}

/** Extract a header value from a Gmail message */
export function getHeader(message: GmailMessage, name: string): string {
  const lowerName = name.toLowerCase()

  // 1. Standard path: payload.headers
  const header = message.payload?.headers?.find(
    (h) => h.name.toLowerCase() === lowerName,
  )
  if (header?.value) return header.value

  // 2. Some MCP responses put headers directly on message
  const msg = message as any
  if (msg.headers && Array.isArray(msg.headers)) {
    const h = msg.headers.find(
      (h: any) => (h.name || h.key || '').toLowerCase() === lowerName,
    )
    if (h?.value) return h.value
  }

  // 3. Some MCP responses flatten common headers as top-level fields
  if (lowerName === 'subject' && msg.subject) return msg.subject
  if (lowerName === 'from' && msg.from) return msg.from
  if (lowerName === 'to' && msg.to) return msg.to
  if (lowerName === 'date' && msg.date) return msg.date
  return ''
}

/** Parse sender into { name, email } */
export function parseSender(from: string): {
  name: string
  email: string
} {
  const match = from.match(/^(.+?)\s*<(.+?)>$/)
  if (match)
    return {
      name: match[1].replace(/"/g, '').trim(),
      email: match[2],
    }
  return {
    name: from,
    email: from,
  }
}

/** Decode HTML entities in a string (for snippets etc.) */
export function decodeHtmlEntities(text: string): string {
  if (!text) return ''
  // Use a map for common entities + handle numeric entities
  const entities: Record<string, string> = {
    '&amp;': '&',
    '&lt;': '<',
    '&gt;': '>',
    '&quot;': '"',
    '&apos;': "'",
    '&nbsp;': ' ',
  }
  return text
    .replace(/&(?:amp|lt|gt|quot|apos|nbsp);/g, (m) => entities[m] || m)
    .replace(/&#(\d+);/g, (_, code) => String.fromCharCode(parseInt(code, 10)))
    .replace(/&#x([0-9a-fA-F]+);/g, (_, hex) =>
      String.fromCharCode(parseInt(hex, 16)),
    )
}

/** Decode base64url encoded body with proper UTF-8 support */
export function decodeBody(data: string): string {
  try {
    // Convert base64url to regular base64
    const base64 = data.replace(/-/g, '+').replace(/_/g, '/')
    // Decode base64 to binary string
    const binaryString = atob(base64)
    // Convert to Uint8Array for proper UTF-8 decoding
    const bytes = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    // Use TextDecoder for proper UTF-8 handling
    return new TextDecoder('utf-8').decode(bytes)
  } catch {
    // Fallback to basic atob if TextDecoder fails
    try {
      return atob(data.replace(/-/g, '+').replace(/_/g, '/'))
    } catch {
      return ''
    }
  }
}

/** Extract the best body content (HTML preferred, fallback to plain text) from a message */
export function extractMessageBody(message: GmailMessage): {
  html: string
  plain: string
} {
  let html = ''
  let plain = ''
  function walkParts(parts?: GmailMessage['payload']['parts']) {
    if (!parts) return
    for (const part of parts) {
      if (part.mimeType === 'text/html' && part.body?.data) {
        html = decodeBody(part.body.data)
      } else if (part.mimeType === 'text/plain' && part.body?.data && !plain) {
        plain = decodeBody(part.body.data)
      }
      if (part.parts) {
        walkParts(part.parts)
      }
    }
  }

  // Check top-level body first
  if (message.payload.mimeType === 'text/html' && message.payload.body?.data) {
    html = decodeBody(message.payload.body.data)
  } else if (
    message.payload.mimeType === 'text/plain' &&
    message.payload.body?.data
  ) {
    plain = decodeBody(message.payload.body.data)
  }

  // Walk parts for multipart messages
  walkParts(message.payload.parts)
  return {
    html,
    plain,
  }
}

/** Extract attachment metadata from a full message */
export function extractAttachments(message: GmailMessage): GmailAttachment[] {
  const attachments: GmailAttachment[] = []
  function walkParts(parts?: GmailMessage['payload']['parts']) {
    if (!parts) return
    for (const part of parts) {
      if (
        part.filename &&
        part.filename.length > 0 &&
        part.body?.attachmentId
      ) {
        attachments.push({
          attachmentId: part.body.attachmentId,
          filename: part.filename,
          mimeType: part.mimeType,
          size: part.body.size || 0,
        })
      }
      if (part.parts) {
        walkParts(part.parts as any)
      }
    }
  }
  walkParts(message.payload.parts)
  return attachments
}

/** Format file size to human readable */
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B'
  const k = 1024
  const sizes = ['B', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i]
}

/** Normalize MCP response for a single Gmail message.
 *  MCP may return: direct message object, { content: [{ text: "..." }] }, or nested structures.
 */
function normalizeMcpMessage(
  raw: any,
  stub?: {
    id: string
    threadId: string
  },
): GmailMessage {
  // 1. If it has payload.headers, it's already a proper Gmail message
  if (raw?.payload?.headers) return raw as GmailMessage

  // 2. MCP tool response format: { content: [{ type: "text", text: "JSON..." }] }
  if (raw?.content && Array.isArray(raw.content)) {
    for (const block of raw.content) {
      if (block.type === 'text' && typeof block.text === 'string') {
        try {
          const parsed = JSON.parse(block.text)
          if (parsed?.payload?.headers) return parsed as GmailMessage
          // Could be nested further
          if (parsed?.result?.payload?.headers)
            return parsed.result as GmailMessage
        } catch {
          // Not JSON — might be plain text summary, skip
        }
      }
    }
  }

  // 3. Double-wrapped: { result: { payload: ... } }
  if (raw?.result?.payload?.headers) return raw.result as GmailMessage

  // 4. Message with top-level fields (some MCPs flatten the structure)
  if (raw?.id && (raw?.snippet || raw?.labelIds || raw?.internalDate)) {
    return {
      id: raw.id,
      threadId: raw.threadId || raw.id,
      labelIds: raw.labelIds || [],
      snippet: raw.snippet || '',
      internalDate: raw.internalDate || String(Date.now()),
      payload: raw.payload || {
        headers: raw.headers || [],
        mimeType: 'text/plain',
      },
      sizeEstimate: raw.sizeEstimate || 0,
    } as GmailMessage
  }

  // 5. Fallback — return a safe minimal message
  const id = raw?.id || stub?.id || 'unknown'
  return {
    id,
    threadId: raw?.threadId || stub?.threadId || id,
    labelIds: raw?.labelIds || [],
    snippet: raw?.snippet || '',
    internalDate: raw?.internalDate || String(Date.now()),
    payload: {
      headers: [],
      mimeType: 'text/plain',
    },
    sizeEstimate: 0,
  } as GmailMessage
}
export const gmail = {
  /**
   * Get the user's profile (email, total messages, threads).
   */
  async getProfile(): Promise<{
    emailAddress: string
    messagesTotal: number
    threadsTotal: number
  }> {
    if (!isMcpConnected())
      throw new Error(
        'Google no está conectado. Conectá tu cuenta desde Conectores.',
      )
    return mcpCallOrThrow('gmail_get_profile')
  },
  /**
   * List recent threads from the inbox.
   */
  async getThreads(
    maxResults: number = 15,
    query?: string,
  ): Promise<GmailThread[]> {
    if (!isMcpConnected())
      throw new Error(
        'Google no está conectado. Conectá tu cuenta desde Conectores.',
      )
    const result = await mcpCallOrThrow<any>('gmail_list_threads', {
      query: query ? `in:inbox ${query}` : 'in:inbox',
      max_results: maxResults,
    })
    console.log(
      '[Gmail] getThreads response type:',
      typeof result,
      Array.isArray(result) ? 'array' : 'object',
    )
    if (Array.isArray(result)) return result
    if (result?.threads && Array.isArray(result.threads)) return result.threads
    if (result?.items && Array.isArray(result.items)) return result.items
    if (result?.data && Array.isArray(result.data)) return result.data
    if (result?.data?.threads && Array.isArray(result.data.threads))
      return result.data.threads
    console.warn(
      '[Gmail] Unexpected threads response, keys:',
      result ? Object.keys(result) : 'null',
    )
    return []
  },
  /**
   * Get a full thread with all messages.
   */
  async getThread(threadId: string): Promise<GmailThread> {
    if (!isMcpConnected())
      throw new Error(
        'Google no está conectado. Conectá tu cuenta desde Conectores.',
      )
    return mcpCallOrThrow('gmail_get_thread', {
      thread_id: threadId,
    })
  },
  /**
   * List recent messages (not grouped by thread).
   */
  async getMessages(
    maxResults: number = 20,
    query?: string,
  ): Promise<GmailMessage[]> {
    if (!isMcpConnected())
      throw new Error(
        'Google no está conectado. Conectá tu cuenta desde Conectores.',
      )
    const result = await mcpCallOrThrow<any>('gmail_list_messages', {
      query: query || undefined,
      max_results: maxResults,
    })

    // Extract message list (may be full objects or just { id, threadId } stubs)
    let messageList: any[] = []
    if (Array.isArray(result)) messageList = result
    else if (result?.messages && Array.isArray(result.messages))
      messageList = result.messages
    else if (result?.items && Array.isArray(result.items))
      messageList = result.items
    else if (result?.data && Array.isArray(result.data))
      messageList = result.data
    else if (result?.data?.messages && Array.isArray(result.data.messages))
      messageList = result.data.messages
    if (messageList.length === 0) return []

    // Check if messages are stubs (only id/threadId) — if so, fetch full details
    const firstMsg = messageList[0]
    const isStub =
      !firstMsg.payload && !firstMsg.snippet && !firstMsg.internalDate
    if (!isStub) return messageList as GmailMessage[]

    // Fetch full details for each message in parallel (limited batch)
    const fullMessages = await Promise.all(
      messageList.slice(0, maxResults).map(async (stub) => {
        try {
          const raw = await mcpCallOrThrow<any>('gmail_get_message', {
            message_id: stub.id,
          })
          return normalizeMcpMessage(raw, stub)
        } catch {
          // Return a minimal fallback so the list doesn't break
          return {
            id: stub.id,
            threadId: stub.threadId,
            labelIds: [],
            snippet: '',
            internalDate: String(Date.now()),
            payload: {
              headers: [],
              mimeType: 'text/plain',
            },
            sizeEstimate: 0,
          } as GmailMessage
        }
      }),
    )
    return fullMessages
  },
  /**
   * Get a single message with full body content.
   */
  async getFullMessage(messageId: string): Promise<GmailMessage> {
    if (!isMcpConnected())
      throw new Error(
        'Google no está conectado. Conectá tu cuenta desde Conectores.',
      )
    const raw = await mcpCallOrThrow<any>('gmail_get_message', {
      message_id: messageId,
    })
    return normalizeMcpMessage(raw, {
      id: messageId,
      threadId: messageId,
    })
  },
  /**
   * Get unread count for INBOX.
   */
  async getUnreadCount(): Promise<number> {
    if (!isMcpConnected())
      throw new Error(
        'Google no está conectado. Conectá tu cuenta desde Conectores.',
      )
    try {
      const result = await mcpCallOrThrow<{
        messagesUnread?: number
      }>('gmail_get_label', {
        label_id: 'INBOX',
      })
      return result.messagesUnread || 0
    } catch {
      return 0
    }
  },
  /**
   * Get labels (folders).
   */
  async getLabels(): Promise<GmailLabel[]> {
    if (!isMcpConnected())
      throw new Error(
        'Google no está conectado. Conectá tu cuenta desde Conectores.',
      )
    const result = await mcpCallOrThrow<any>('gmail_list_labels')
    if (Array.isArray(result)) return result
    if (result?.labels && Array.isArray(result.labels)) return result.labels
    if (result?.items && Array.isArray(result.items)) return result.items
    if (result?.data && Array.isArray(result.data)) return result.data
    console.warn(
      '[Gmail] Unexpected labels response, keys:',
      result ? Object.keys(result) : 'null',
    )
    return []
  },
  /**
   * Get an attachment's data by messageId and attachmentId.
   */
  async getAttachment(
    messageId: string,
    attachmentId: string,
  ): Promise<{
    size: number
    data: string
  }> {
    if (!isMcpConnected())
      throw new Error(
        'Google no está conectado. Conectá tu cuenta desde Conectores.',
      )
    return mcpCallOrThrow('gmail_get_attachment', {
      message_id: messageId,
      attachment_id: attachmentId,
    })
  },
  /**
   * Download an attachment and trigger browser download.
   */
  async downloadAttachment(
    messageId: string,
    attachment: GmailAttachment,
  ): Promise<void> {
    const result = await this.getAttachment(messageId, attachment.attachmentId)
    // Convert base64url to regular base64
    const base64 = result.data.replace(/-/g, '+').replace(/_/g, '/')
    const binaryString = atob(base64)
    const bytes = new Uint8Array(binaryString.length)
    for (let i = 0; i < binaryString.length; i++) {
      bytes[i] = binaryString.charCodeAt(i)
    }
    const blob = new Blob([bytes], {
      type: attachment.mimeType,
    })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = attachment.filename
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    URL.revokeObjectURL(url)
  },
  /**
   * Search emails with Gmail query syntax.
   */
  async search(
    query: string,
    maxResults: number = 10,
  ): Promise<GmailMessage[]> {
    return this.getMessages(maxResults, query)
  },
}
